import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal, computed, effect, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule, type TableLazyLoadEvent } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { FileUploadModule } from 'primeng/fileupload';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { AccordionModule } from 'primeng/accordion';
import { ContextMenuModule } from 'primeng/contextmenu';
import { MessageModule } from 'primeng/message';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TabsModule } from 'primeng/tabs';
import { MarkdownPipe } from '@/shared/pipes/markdown.pipe';
import { CloudApiService, Package, PackageDetails, PackageVersion, PackageVersionBuildFilter, PackageVersionCleanupResult, PackageVersionInUseError, PackageVersionUsage, CompatibleDevice } from '@/cloud/services/cloud-api.service';
import { deviceDisplayName } from '@/cloud/services/device-display';
import { TenantService } from '@/cloud/services/tenant.service';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { BreadcrumbsComponent, BreadcrumbItem } from '@/shared/breadcrumbs/breadcrumbs.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { TranslationService } from '@/layout/service/translation.service';
import { PackageLinks } from './package-links';
import { PackageIssues } from './package-issues';
import { normalizeGitHubRepository } from '@/cloud/models/github.models';
import { clampProgramSlot, fgetfileCommand, programPath, progloadCommand } from '@/cloud/services/console-commands';


interface SelectableDevice extends CompatibleDevice {
  selected: boolean;
}

interface SelectableVersion extends PackageVersion {
  selected: boolean;
}

/** "Running on Lobby, Boardroom and 3 more · 1 pending deployment", or '' when the version is not in use. */
export function describeVersionUsage(usage: PackageVersionUsage | null | undefined): string {
  if (!usage?.inUse) return '';
  const parts: string[] = [];
  if (usage.runningOnCount > 0) {
    const names = usage.runningOn.slice(0, 3).map(d => d.deviceName);
    const more = usage.runningOnCount - names.length;
    parts.push(`Running on ${names.join(', ')}${more > 0 ? ` and ${more} more` : ''}`);
  }
  if (usage.pendingDeploymentCount > 0) {
    parts.push(`${usage.pendingDeploymentCount} pending deployment${usage.pendingDeploymentCount === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}

/** For a 409 `version_in_use` from deleting a version or package: which versions are in use and where; otherwise null. */
export function versionInUseMessage(err: unknown): string | null {
  const response = err as { status?: number; error?: PackageVersionInUseError } | null;
  if (response?.status !== 409 || response.error?.code !== 'version_in_use') return null;
  const versions = response.error.versions ?? [];
  const details = versions.slice(0, 3).map(v => `v${v.assemblyVersion}: ${describeVersionUsage(v.usage)}`);
  if (versions.length > 3) details.push(`and ${versions.length - 3} more version(s)`);
  return [response.error.error, ...details].join('. ');
}

interface NamespaceLocation {
  id: string;
  appNamespaceId: string;
  parentId?: string | null;
  name?: string;
}

interface LocationDeviceRow {
  id: string;
  name: string;
  depth: number;
  devicesAtLocation: SelectableDevice[];
  devicesInSubtree: SelectableDevice[];
}

// Matches timezone-qualified timestamps (UTC suffix or explicit ±HH:mm offset).
const TIMEZONE_SUFFIX_REGEX = /[Zz]|[+-]\d{2}:\d{2}$/;
const PACKAGE_TABS = ['releases', 'issues', 'links'] as const;
const VERSION_SEARCH_DEBOUNCE_MS = 300;
type PackageTab = (typeof PACKAGE_TABS)[number];

@Component({
  selector: 'app-packages',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    BreadcrumbsComponent,
    EmptyStateComponent,
    LoadingSkeletonComponent,
    FormsModule,
    ButtonModule,
    TableModule,
    DialogModule,
    SelectModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    FileUploadModule,
    TagModule,
    ProgressSpinnerModule,
    TooltipModule,
    ConfirmDialogModule,
    CheckboxModule,
    DatePickerModule,
    AccordionModule,
    ContextMenuModule,
    MessageModule,
    SelectButtonModule,
    TabsModule,
    MarkdownPipe,
    PackageLinks, PackageIssues
  ],
  providers: [ConfirmationService],
  templateUrl: './packages.html',
  styleUrl: './packages.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Packages implements OnInit {
  private api = inject(CloudApiService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private tenants = inject(TenantService);
  readonly t = inject(TranslationService).t;
  private listRequest = 0;
  private detailRequest = 0;
  private versionsRequest = 0;
  private cleanupRequest = 0;
  private versionSearchTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly destroyRef = inject(DestroyRef);
  readonly libraryFirst = signal(0);
  readonly packageLoadErrors = signal<Set<string>>(new Set());
  readonly libraryError = computed(() => this.packageLoadErrors().has(this.tenants.selectedTenantId() ?? ''));
  readonly libraryLoading = computed(() => this.api.namespaces() === null || this.loadingAllPackages());
  readonly tenantPackages = computed(() => this.packagesMap().get(this.tenants.selectedTenantId() ?? '') ?? []);
  readonly libraryPackages = computed(() => this.filteredTreeData().flatMap(item => item.packages));
  readonly libraryTenantName = computed(() => this.namespaces().find(ns => ns.id === this.tenants.selectedTenantId())?.name ?? this.t()('management.selectTenant'));
  readonly latestPackageUpdate = computed(() => {
    const timestamp = this.tenantPackages().reduce((latest, pkg) => {
      const time = Date.parse(pkg.updatedUtc || pkg.createdUtc);
      return Number.isFinite(time) ? Math.max(latest, time) : latest;
    }, 0);
    return timestamp ? new Date(timestamp).toISOString() : null;
  });
  readonly libraryBreadcrumbs = computed<BreadcrumbItem[]>(() => [
    { label: this.t()('management.dashboard'), routerLink: '/dashboard' },
    { label: this.t()('management.packages'), routerLink: '/packages' }
  ]);

  // State
  namespaces = computed(() => this.api.namespaces() || []);
  /** Mirrors the tenant chosen in the topbar (TenantService); kept as a signal for the existing call sites. */
  selectedNamespace = signal<any>(null);
  packages = signal<Package[]>([]);
  packageFilter = signal('');
  loading = signal(false);

  // Package catalog
  packagesMap = signal<Map<string, Package[]>>(new Map());
  loadingAllPackages = signal(false);
  treeFilter = signal('');
  selectedPackageId = signal<string | null>(null);
  uploadNamespace = signal<any>(null);
  pendingSelectPkg = signal<{ name: string; nsId: string } | null>(null);

  // Computed Tree Data
  filteredTreeData = computed(() => {
    const term = this.treeFilter().trim().toLowerCase();
    // Only the tenant selected in the topbar is listed.
    const selectedId = this.tenants.selectedTenantId();
    const nsList = this.namespaces().filter((ns) => ns.id === selectedId);
    const map = this.packagesMap();
    
    return nsList.map(ns => {
      const allPkgs = map.get(ns.id) || [];
      const filteredPkgs = allPkgs.filter(pkg => {
        if (!term) return true;
        return pkg.name.toLowerCase().includes(term) ||
               pkg.programNamespace.toLowerCase().includes(term) ||
               (pkg.description || '').toLowerCase().includes(term);
      });
      
      const nsMatches = !term || ns.name.toLowerCase().includes(term);
      
      // If namespace matches, show all its packages. Otherwise, show only matching packages.
      const pkgsToDisplay = nsMatches ? allPkgs : filteredPkgs;
      
      return {
        namespace: ns,
        packages: pkgsToDisplay,
        visible: nsMatches || filteredPkgs.length > 0,
        pkgCount: allPkgs.length
      };
    }).filter(item => item.visible);
  });

  uploadableNamespaces = computed(() => {
    return this.namespaces().filter(ns => this.api.hasNamespaceRole(ns.id, [1, 2]));
  });

  // Package details inline
  showPackageDialog = signal(false);
  selectedPackage = signal<PackageDetails | null>(null);
  loadingPackage = signal(false);
  // Version history: paged, searched and filtered on the server so every version is reachable
  versionsPageSize = signal(10);
  versionsFirst = signal(0);
  versionsPage = signal<PackageVersion[]>([]);
  versionsTotal = signal(0);
  loadingVersions = signal(false);
  /** Selected versions by id; kept across pages, cleared when the filters change. */
  selectedVersions = signal<Map<string, PackageVersion>>(new Map());
  deletingVersions = signal(false);
  versionFilter = signal('');
  versionBuildFilter = signal<PackageVersionBuildFilter>('all');
  activeDetailTab = signal<PackageTab>('releases');
  showCleanupDialog = signal(false);
  cleanupBefore = signal<Date | null>(null);
  cleanupDebug = signal(false);
  /** Keep this many of the newest versions; everything older matches. Null: not a criterion. */
  cleanupKeepNewest = signal<number | null>(null);
  cleanupInProgress = signal(false);
  /** Server dry run of the clean-up criteria across the package's whole history. */
  cleanupPreview = signal<PackageVersionCleanupResult | null>(null);
  cleanupPreviewLoading = signal(false);
  cleanupPreviewError = signal(false);

  // Computed: the loaded page with selection state
  displayedVersions = computed<SelectableVersion[]>(() => {
    const selected = this.selectedVersions();
    return this.versionsPage().map(v => ({ ...v, selected: selected.has(v.id) }));
  });

  filteredPackages = computed(() => {
    const term = this.packageFilter().trim().toLowerCase();
    const allPackages = this.packages();
    if (!term) return allPackages;
    return allPackages.filter(pkg =>
      pkg.name.toLowerCase().includes(term)
      || pkg.programNamespace.toLowerCase().includes(term)
      || (pkg.description || '').toLowerCase().includes(term)
    );
  });

  canCleanUpVersions = computed(() => !!this.cleanupBefore() || this.cleanupDebug() || (this.cleanupKeepNewest() ?? 0) >= 1);

  // Computed: selected versions count (across pages)
  selectedVersionsCount = computed(() => this.selectedVersions().size);

  // Computed: all displayed versions selected
  allVersionsSelected = computed(() => {
    const versions = this.displayedVersions();
    return versions.length > 0 && versions.every(v => v.selected);
  });

  // Computed: some displayed versions selected (for indeterminate state)
  someVersionsSelected = computed(() => {
    const versions = this.displayedVersions();
    const selectedCount = versions.filter(v => v.selected).length;
    return selectedCount > 0 && selectedCount < versions.length;
  });

  // Upload dialog
  showUploadDialog = signal(false);
  uploadFile = signal<File | null>(null);
  uploadReleaseNotes = '';
  uploading = signal(false);

  // Edit package dialog
  showEditDialog = signal(false);
  editPackageName = '';
  editPackageDescription = '';
  editPackageRepository = '';
  savingEdit = signal(false);

  // Deploy dialog - updated for multi-device support
  /** Template helper — see deviceDisplayName for the precedence. */
  displayName = deviceDisplayName;
  showDeployDialog = signal(false);
  selectedVersion = signal<PackageVersion | null>(null);
  compatibleDevices = signal<SelectableDevice[]>([]);
  loadingDevices = signal(false);
  deploying = signal(false);
  deviceFilter = signal('');
  preSelectLocationId = signal<string | null>(null);
  preSelectLocationName = signal<string | null>(null);

  // Location package picker dialog
  showLocationPackagePickerDialog = signal(false);
  locationPackages = signal<{ package: Package; deviceCount: number }[]>([]);
  loadingLocationPackages = signal(false);

  // Schedule deployment dialog
  showScheduleDialog = signal(false);
  scheduledDateTime: Date | null = null;
  minScheduleDate = new Date(); // Can't schedule in the past

  // Version release notes dialog
  showVersionReleaseNotesDialog = signal(false);
  versionReleaseNotes = signal<{ version: string; notes: string } | null>(null);

  // Version context menu
  versionMenuItems = signal<MenuItem[]>([]);

  // Console command dialog
  showConsoleCommandDialog = signal(false);
  consoleCommandVersion = signal<PackageVersion | null>(null);
  consoleCommandDownloadUrl = signal<string | null>(null);
  consoleCommandExpiresUtc = signal<string | null>(null);
  consoleCommandLoading = signal(false);
  consoleCommandProgramSlot = signal(1);
  /** "quick": a one-off storage URL in the command. "link": explains and hands off to the share-links dialog. */
  consoleCommandMode = signal<'quick' | 'link'>('quick');
  consoleCommandExpiry = signal(60);
  consoleCommandError = signal(false);
  private consoleRequest = 0;
  readonly packageLinks = viewChild(PackageLinks);
  readonly consoleCommandModes = computed(() => [
    { label: this.t()('console.modeQuick'), value: 'quick' },
    ...(this.selectedPackage() && this.canManagePackage(this.selectedPackage()!.package) ? [{ label: this.t()('console.modeLink'), value: 'link' }] : [])
  ]);
  readonly consoleCommandExpiryOptions = [
    { label: '15 min', value: 15 },
    { label: '1 h', value: 60 },
    { label: '6 h', value: 360 },
    { label: '24 h', value: 1440 }
  ];

  // Computed: destination path on the processor for the selected program slot
  consoleCommandProgramPath = computed(() => programPath(this.consoleCommandProgramSlot()));

  // Computed: fgetfile command to download the package to the processor
  consoleCommandFgetfileCommand = computed(() => fgetfileCommand(this.consoleCommandDownloadUrl(), this.consoleCommandProgramSlot()));

  // Computed: progload command to load the downloaded program into the selected slot
  consoleCommandProgloadCommand = computed(() => progloadCommand(this.consoleCommandProgramSlot()));

  consoleCommandValidUntil = computed(() => {
    const expires = this.consoleCommandExpiresUtc();
    return expires ? this.t()('console.validUntil').replace('{time}', new Date(expires).toLocaleString()) : '';
  });

  // Computed: selected devices count
  selectedDevicesCount = computed(() => {
    return this.compatibleDevices().filter(d => d.selected).length;
  });

  // Computed: all selected
  allDevicesSelected = computed(() => {
    const devices = this.filteredCompatibleDevices().filter(d => this.deviceNeedsUpdate(d));
    return devices.length > 0 && devices.every(d => d.selected);
  });

  // Computed: some selected (for indeterminate state)
  someDevicesSelected = computed(() => {
    const devices = this.filteredCompatibleDevices().filter(d => this.deviceNeedsUpdate(d));
    const selectedCount = devices.filter(d => d.selected).length;
    return selectedCount > 0 && selectedCount < devices.length;
  });

  // Computed: detect duplicate processorIds among selected VC-4 server devices
  // When multiple VC-4 instances share the same processorId, updating one will update all
  duplicateProcessorWarning = computed(() => {
    const selectedDevices = this.compatibleDevices().filter(d => d.selected);

    // Only check Server (VC-4) devices - Appliances are fine
    const serverDevices = selectedDevices.filter(d => d.devicePlatform === 'Server' && d.processorId);

    if (serverDevices.length < 2) {
      return null;
    }

    // Group by processorId
    const byProcessorId = new Map<string, SelectableDevice[]>();
    for (const device of serverDevices) {
      const pid = device.processorId!;
      const existing = byProcessorId.get(pid) || [];
      existing.push(device);
      byProcessorId.set(pid, existing);
    }

    // Find duplicates
    const duplicates: { processorId: string; devices: SelectableDevice[] }[] = [];
    for (const [processorId, devices] of byProcessorId) {
      if (devices.length > 1) {
        duplicates.push({ processorId, devices });
      }
    }

    if (duplicates.length === 0) {
      return null;
    }

    // Build warning message
    const deviceNames = duplicates.flatMap(d => d.devices.map(dev => dev.name));
    return `Warning: ${deviceNames.join(', ')} are VC-4 instances on the same server. Updating one will update all instances on that server.`;
  });

  private currentNamespaceLocations = computed<NamespaceLocation[]>(() => {
    const nsId = this.selectedNamespace()?.id;
    const locations = (this.api.locations() || []) as NamespaceLocation[];
    if (!nsId) return [];
    return locations.filter(l => l?.appNamespaceId === nsId);
  });

  private locationNameById = computed(() => {
    const map = new Map<string, string>();
    for (const loc of this.currentNamespaceLocations()) {
      if (loc?.id) map.set(loc.id, loc.name || loc.id);
    }
    return map;
  });

  filteredCompatibleDevices = computed(() => {
    const term = this.deviceFilter().trim().toLowerCase();
    const locationNameMap = this.locationNameById();
    const devices = this.compatibleDevices();
    if (!term) return devices;
    return devices.filter(d => {
      const locationName = d.locationNodeId ? (locationNameMap.get(d.locationNodeId) || '') : '';
      return deviceDisplayName(d).toLowerCase().includes(term)
        || (d.model || '').toLowerCase().includes(term)
        || (d.version || '').toLowerCase().includes(term)
        || locationName.toLowerCase().includes(term);
    });
  });

  locationDeviceRows = computed<LocationDeviceRow[]>(() => {
    const devices = this.filteredCompatibleDevices();
    const locations = this.currentNamespaceLocations();
    const locationsByParent = new Map<string | null, NamespaceLocation[]>();
    const locationsById = new Map<string, NamespaceLocation>();
    const devicesByLocation = new Map<string, SelectableDevice[]>();
    const unassigned: SelectableDevice[] = [];

    for (const loc of locations) {
      if (!loc?.id) continue;
      locationsById.set(loc.id, loc);
    }

    for (const loc of locations) {
      if (!loc?.id) continue;
      const parentKey = loc.parentId && locationsById.has(loc.parentId) ? loc.parentId : null;
      const list = locationsByParent.get(parentKey) || [];
      list.push(loc);
      locationsByParent.set(parentKey, list);
    }

    for (const list of locationsByParent.values()) {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    for (const device of devices) {
      const locationId = device.locationNodeId;
      if (locationId && locationsById.has(locationId)) {
        const list = devicesByLocation.get(locationId) || [];
        list.push(device);
        devicesByLocation.set(locationId, list);
      } else {
        unassigned.push(device);
      }
    }

    for (const list of devicesByLocation.values()) {
      list.sort((a, b) => deviceDisplayName(a).localeCompare(deviceDisplayName(b)));
    }
    unassigned.sort((a, b) => deviceDisplayName(a).localeCompare(deviceDisplayName(b)));

    const buildRows = (parentId: string | null, depth: number): LocationDeviceRow[] => {
      const children = locationsByParent.get(parentId) || [];
      return children.flatMap((loc: NamespaceLocation) => {
        const childRows = buildRows(loc.id, depth + 1);
        const devicesAtLocation = devicesByLocation.get(loc.id) || [];
        const devicesInSubtree = [
          ...devicesAtLocation,
          ...childRows.flatMap(r => r.devicesInSubtree)
        ];
        if (devicesInSubtree.length === 0) {
          return [];
        }
        return [{
          id: loc.id,
          name: loc.name || loc.id,
          depth,
          devicesAtLocation,
          devicesInSubtree
        }, ...childRows];
      });
    };

    const rows = buildRows(null, 0.8);
    if (unassigned.length > 0) {
      rows.push({
        id: '__unassigned__',
        name: 'Unassigned',
        depth: 0,
        devicesAtLocation: unassigned,
        devicesInSubtree: unassigned
      });
    }

    return rows;
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      ++this.listRequest; ++this.detailRequest; ++this.consoleRequest; ++this.versionsRequest; ++this.cleanupRequest;
      clearTimeout(this.versionSearchTimer);
    });
    effect(() => {
      this.treeFilter();
      this.tenants.selectedTenantId();
      this.libraryFirst.set(0);
    });
    // Effect to load packages for all namespaces when they become available
    effect(() => {
      const nsList = this.namespaces();
      this.loadAllPackages(nsList);
    });

    // Follow the tenant chosen in the topbar. Clear a package selection that belongs to another tenant.
    effect(() => {
      const selectedId = this.tenants.selectedTenantId();
      const ns = this.namespaces().find((n) => n.id === selectedId) ?? null;
      const current = this.selectedNamespace();
      if (current?.id !== ns?.id) {
        this.selectedNamespace.set(ns);
        if (current) this.closePackageDialog();
      }
    });
  }

  loadAllPackages(nsList: any[]): void {
    const request = ++this.listRequest;
    this.loadingAllPackages.set(true);
    this.packageLoadErrors.set(new Set());
    const map = new Map<string, Package[]>();
    const errors = new Set<string>();
    const promises = nsList.map(ns => 
      this.api.getPackages(ns.id)
        .then(pkgs => {
          const pkgsWithNs = pkgs.map(p => ({ ...p, appNamespaceId: ns.id }));
          map.set(ns.id, pkgsWithNs);
        })
        .catch(err => {
          console.error(`Failed to load packages for namespace ${ns.name || ns.id}`, err);
          errors.add(ns.id);
        })
    );

    Promise.all(promises).then(() => {
      if (request !== this.listRequest) return;
      this.packagesMap.set(map);
      this.packageLoadErrors.set(errors);
      this.loadingAllPackages.set(false);
      this.libraryFirst.set(0);
      
      // Auto-select pending package if set
      const pending = this.pendingSelectPkg();
      if (pending) {
        const pkgs = map.get(pending.nsId) || [];
        const found = pkgs.find(p => p.name === pending.name || p.programNamespace === pending.name);
        if (found) {
          this.tenants.select(pending.nsId);
          this.selectPackageFromTree(found);
          this.pendingSelectPkg.set(null);
          return;
        }
      }

      // Auto-select package or namespace if needed
      const currentSelectedPkgId = this.selectedPackageId();
      if (currentSelectedPkgId) {
        let found = false;
        for (const pkgs of map.values()) {
          const pkg = pkgs.find(p => p.id === currentSelectedPkgId);
          if (pkg) {
            this.selectPackageFromTree(pkg);
            found = true;
            break;
          }
        }
        if (!found) {
          this.closePackageDialog();
        }
      }
    });
  }

  selectPackageFromTree(pkg: Package, namespace?: any): void {
    const request = ++this.detailRequest;
    this.selectedPackageId.set(pkg.id);
    this.selectedPackage.set(null);
    this.loadingPackage.set(true);
    this.resetVersions();
    this.activeDetailTab.set('releases');
    void this.loadVersions();

    // Find and set namespace for compat stability
    const ns = namespace || this.namespaces().find(n => n.id === pkg.appNamespaceId || n.name === pkg.programNamespace);
    if (ns) {
      this.selectedNamespace.set(ns);
    }

    this.api.getPackageDetails(pkg.id, 20)
      .then(details => {
        if (request !== this.detailRequest || this.selectedPackageId() !== pkg.id || this.tenants.selectedTenantId() !== pkg.appNamespaceId) return;
        this.selectedPackage.set(details);
        this.loadingPackage.set(false);
      })
      .catch(err => {
        if (request !== this.detailRequest) return;
        console.error('Failed to load package details', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error || 'Failed to load package details'
        });
        this.selectedPackageId.set(null);
        this.selectedPackage.set(null);
        this.loadingPackage.set(false);
      });
  }

  ngOnInit(): void {
    const deployPackageId = this.route.snapshot.queryParamMap.get('deploy');
    const nsId = this.route.snapshot.queryParamMap.get('ns');
    const preSelectLocation = this.route.snapshot.queryParamMap.get('preSelectLocation');
    const locationName = this.route.snapshot.queryParamMap.get('locationName');

    if (preSelectLocation) {
      this.preSelectLocationId.set(preSelectLocation);
      this.preSelectLocationName.set(locationName);
    }

    // A deep link (e.g. "Update" from the location tree) may name the tenant explicitly.
    if (nsId) {
      this.tenants.select(nsId);
    }

    this.router.navigate([], { queryParams: {}, replaceUrl: true });

    if (deployPackageId) {
      this.openDeployForPackageId(deployPackageId);
    } else if (preSelectLocation && nsId) {
      this.autoResolveLocationPackages(nsId, preSelectLocation);
    }
  }

  async openDeployForPackageId(packageId: string): Promise<void> {
    try {
      const details = await this.api.getPackageDetails(packageId, 1);
      this.selectedPackage.set(details);
      if (details.latestVersion) {
        this.openDeployDialog({ ...details.latestVersion, isLatest: true });
      } else {
        this.messageService.add({
          severity: 'info',
          summary: 'No Versions',
          detail: 'This package has no versions available to deploy.'
        });
      }
    } catch (err: any) {
      console.error('Failed to load package for deploy', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: err?.error || 'Failed to load package details'
      });
    }
  }

  private async autoResolveLocationPackages(namespaceId: string, locationId: string): Promise<void> {
    this.loadingLocationPackages.set(true);
    try {
      const packages = await this.api.getPackages(namespaceId);
      if (packages.length === 0) {
        this.messageService.add({ severity: 'info', summary: 'No Packages', detail: 'No packages found in this namespace.' });
        return;
      }

      // Fetch location details to resolve parent/control devices which might be located elsewhere
      const parentDeviceIds = new Set<string>();
      try {
        const locDetails = await this.api.getLocationDetails(locationId);
        if (locDetails && locDetails.parentDevices) {
          locDetails.parentDevices.forEach((d: any) => {
            if (d.id) {
              parentDeviceIds.add(d.id);
            }
          });
        }
      } catch (err) {
        console.error('Failed to load location details for parent device resolution', err);
      }

      const subtreeIds = this.getLocationSubtreeIds(locationId);
      const results = await Promise.all(
        packages.map(async (pkg) => {
          const devices = await this.api.getCompatibleDevices(pkg.id);
          const atLocation = devices.filter(d => 
            (d.locationNodeId && subtreeIds.has(d.locationNodeId)) || 
            parentDeviceIds.has(d.id)
          );
          return { package: pkg, deviceCount: atLocation.length };
        })
      );

      const matching = results.filter(r => r.deviceCount > 0);
      if (matching.length === 0) {
        this.messageService.add({
          severity: 'info',
          summary: 'No Matching Devices',
          detail: `No compatible devices found at ${this.preSelectLocationName() || 'this location'}.`
        });
      } else if (matching.length === 1) {
        await this.openDeployForPackageId(matching[0].package.id);
      } else {
        this.locationPackages.set(matching);
        this.showLocationPackagePickerDialog.set(true);
      }
    } catch (err: any) {
      console.error('Failed to resolve packages for location', err);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error || 'Failed to resolve packages for location' });
    } finally {
      this.loadingLocationPackages.set(false);
    }
  }

  async selectLocationPackage(pkg: Package): Promise<void> {
    this.showLocationPackagePickerDialog.set(false);
    await this.openDeployForPackageId(pkg.id);
  }

  selectNamespace(ns: any): void {
    this.selectedNamespace.set(ns);
    if (ns?.id) this.tenants.select(ns.id);
    this.loadPackages();
  }

  loadPackages(): void {
    const nsList = this.namespaces();
    if (nsList && nsList.length > 0) {
      this.loadAllPackages(nsList);
    }
  }

  // View package details
  viewPackage(pkg: Package): void {
    this.selectPackageFromTree(pkg);
  }

  closePackageDialog(): void {
    this.closeConsoleCommandDialog();
    ++this.detailRequest;
    this.loadingPackage.set(false);
    this.showPackageDialog.set(false);
    this.selectedPackageId.set(null);
    this.selectedPackage.set(null);
    this.resetVersions();
  }

  /** Clears the version table, its filters and selection, dropping any request still in flight. */
  private resetVersions(): void {
    ++this.versionsRequest;
    clearTimeout(this.versionSearchTimer);
    this.versionsPage.set([]);
    this.versionsTotal.set(0);
    this.versionsFirst.set(0);
    this.loadingVersions.set(false);
    this.selectedVersions.set(new Map());
    this.versionFilter.set('');
    this.versionBuildFilter.set('all');
  }

  /** Loads the current page of the selected package's versions with the current filters. */
  async loadVersions(): Promise<void> {
    const packageId = this.selectedPackageId();
    if (!packageId) return;
    const request = ++this.versionsRequest;
    this.loadingVersions.set(true);
    try {
      const page = await this.api.getPackageVersions(packageId, {
        search: this.versionFilter(),
        build: this.versionBuildFilter(),
        offset: this.versionsFirst(),
        limit: this.versionsPageSize()
      });
      if (request !== this.versionsRequest) return;
      // Past the end (e.g. the last page was deleted): step back to the last page that has versions.
      if (page.items.length === 0 && page.total > 0 && this.versionsFirst() > 0) {
        const rows = this.versionsPageSize();
        this.versionsFirst.set(Math.floor((page.total - 1) / rows) * rows);
        return this.loadVersions();
      }
      this.versionsPage.set(page.items);
      this.versionsTotal.set(page.total);
    } catch (err: any) {
      if (request !== this.versionsRequest) return;
      console.error('Failed to load package versions', err);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error || 'Failed to load package versions' });
      this.versionsPage.set([]);
      this.versionsTotal.set(0);
    } finally {
      if (request === this.versionsRequest) this.loadingVersions.set(false);
    }
  }

  onVersionsLazyLoad(event: TableLazyLoadEvent): void {
    const first = event.first ?? 0;
    const rows = event.rows || this.versionsPageSize();
    // The table also emits on init; skip when that page is already loaded or loading.
    if (first === this.versionsFirst() && rows === this.versionsPageSize() && (this.loadingVersions() || this.versionsPage().length)) return;
    this.versionsFirst.set(first);
    this.versionsPageSize.set(rows);
    void this.loadVersions();
  }

  /** Search box: debounced, then reloads from the first page. */
  setVersionFilter(value: string): void {
    this.versionFilter.set(value);
    clearTimeout(this.versionSearchTimer);
    this.versionSearchTimer = setTimeout(() => this.reloadVersionsFromStart(), VERSION_SEARCH_DEBOUNCE_MS);
  }

  setVersionBuildFilter(value: PackageVersionBuildFilter): void {
    this.versionBuildFilter.set(value);
    clearTimeout(this.versionSearchTimer);
    this.reloadVersionsFromStart();
  }

  private reloadVersionsFromStart(): void {
    this.versionsFirst.set(0);
    this.selectedVersions.set(new Map());
    void this.loadVersions();
  }

  canManagePackages(): boolean {
    const ns = this.selectedNamespace();
    if (!ns) return false;
    return this.api.hasNamespaceRole(ns.id, [1, 2]); // Writer or Admin
  }

  canManageAnyNamespace(): boolean {
    const nsList = this.namespaces();
    return nsList.some(ns => this.api.hasNamespaceRole(ns.id, [1, 2]));
  }

  canManagePackage(pkg: Package): boolean {
    const nsId = pkg.appNamespaceId || this.selectedNamespace()?.id;
    if (!nsId) return false;
    return this.api.hasNamespaceRole(nsId, [1, 2]);
  }

  /** Template helper: tooltip text for a version that cannot be deleted. */
  describeUsage = describeVersionUsage;

  /** Deleting versions (one, a selection or a clean-up) needs packages.delete on the package's tenant. */
  canDeleteVersions(pkg: Package): boolean {
    return this.api.hasCapability(pkg.appNamespaceId || this.selectedNamespace()?.id, 'packages.delete');
  }

  // Show release notes for a specific version
  showReleaseNotes(version: PackageVersion): void {
    this.versionReleaseNotes.set({
      version: version.assemblyVersion,
      notes: version.releaseNotes || 'No release notes available.'
    });
    this.showVersionReleaseNotesDialog.set(true);
  }

  // Build and show version context menu
  onVersionClick(event: MouseEvent, version: PackageVersion, menu: any): void {
    event.preventDefault();
    event.stopPropagation();

    const items: MenuItem[] = [];

    // Release notes (always available if present)
    if (version.releaseNotes) {
      items.push({
        label: 'View Release Notes',
        icon: 'pi pi-file-edit',
        styleClass: 'menu-item-notes',
        command: () => this.showReleaseNotes(version)
      });
    }

    if (items.length > 0) {
      items.push({ separator: true });
    }

    items.push({
      label: 'Download',
      icon: 'pi pi-download',
      styleClass: 'menu-item-download',
      command: () => this.downloadVersion(version)
    });

    items.push({
      label: 'Console Command',
      icon: 'pi pi-desktop',
      styleClass: 'menu-item-console-command',
      command: () => this.openConsoleCommandDialog(version)
    });

    // Management actions (only for users with permissions)
    if (this.canManagePackages()) {
      if (items.length > 0) {
        items.push({ separator: true });
      }

      if (!version.isLatest) {
        items.push({
          label: 'Set as Latest',
          icon: 'pi pi-check',
          styleClass: 'menu-item-latest',
          command: () => this.setLatestVersion(version)
        });
      }

      items.push({
        label: 'Deploy',
        icon: 'pi pi-cloud-upload',
        styleClass: 'menu-item-deploy',
        command: () => this.openDeployDialog(version)
      });

      const pkg = this.selectedPackage()?.package;
      if (pkg && this.canDeleteVersions(pkg)) {
        const inUse = !!version.usage?.inUse;
        items.push({
          label: inUse ? 'Delete (in use)' : 'Delete',
          icon: 'pi pi-trash',
          styleClass: 'menu-item-delete',
          disabled: inUse,
          title: inUse ? describeVersionUsage(version.usage) : undefined,
          command: () => this.deleteVersion(version)
        });
      }
    }

    this.versionMenuItems.set(items);

    // Show menu at mouse position
    if (menu && typeof menu.show === 'function') {
      menu.show(event);
    }
  }

  // Upload
  openUploadDialog(): void {
    this.uploadFile.set(null);
    this.uploadReleaseNotes = '';
    
    // Pre-populate uploadNamespace with current selectedNamespace if the user can upload to it
    const activeNs = this.selectedNamespace();
    const canUploadToActive = activeNs && this.api.hasNamespaceRole(activeNs.id, [1, 2]);
    if (canUploadToActive) {
      this.uploadNamespace.set(activeNs);
    } else {
      const uploadable = this.uploadableNamespaces();
      this.uploadNamespace.set(uploadable.length > 0 ? uploadable[0] : null);
    }
    
    this.showUploadDialog.set(true);
    this.isDragOver.set(false);
  }

  onFileSelect(event: any): void {
    const file = event.files?.[0];
    if (file) {
      this.uploadFile.set(file);
    }
  }

  // Drag and drop handlers for upload
  isDragOver = signal(false);

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.toLowerCase().endsWith('.cpz')) {
        this.uploadFile.set(file);
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Invalid File Type',
          detail: 'Only Crestron .cpz program packages are supported.'
        });
      }
    }
  }

  onFileSelected(event: any): void {
    const file = event.target?.files?.[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.cpz')) {
        this.uploadFile.set(file);
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Invalid File Type',
          detail: 'Only Crestron .cpz program packages are supported.'
        });
      }
    }
  }

  unifiedStatus(device: CompatibleDevice): string {
    const devAny = device as any;
    const status = devAny.status !== undefined && devAny.status !== null ? devAny.status : devAny.Status;
    
    if (status === undefined || status === null) {
      return 'Unknown';
    }

    const statusStr = String(status).trim().toLowerCase();
    if (statusStr === '1' || statusStr === 'active') {
      return 'Active';
    }
    if (statusStr === '0' || statusStr === 'inactive' || statusStr === '2' || statusStr === 'pending') {
      return 'Inactive';
    }
    if (statusStr === '3' || statusStr === 'error') {
      return 'Inactive';
    }
    return 'Unknown';
  }

  statusClass(device: CompatibleDevice): string {
    const label = this.unifiedStatus(device);
    switch (label) {
      case 'Active': return 'status-active';
      case 'Inactive': return 'status-inactive';
      default: return 'status-unknown';
    }
  }

  compareVersions(v1: string | null | undefined, v2: string | null | undefined): number {
    const clean = (v: string | null | undefined) => {
      if (!v) return [];
      return v.replace(/[^0-9.]/g, '').split('.').map(Number);
    };

    const parts1 = clean(v1);
    const parts2 = clean(v2);

    const maxLen = Math.max(parts1.length, parts2.length);
    for (let i = 0; i < maxLen; i++) {
      const p1 = parts1[i] || 0;
      const p2 = parts2[i] || 0;
      if (p1 < p2) return -1;
      if (p1 > p2) return 1;
    }
    return 0;
  }

  deviceNeedsUpdate(device: CompatibleDevice): boolean {
    const selectedVer = this.selectedVersion();
    if (!selectedVer) return false;

    const deviceVer = device.version;
    if (!deviceVer) return true;

    return this.compareVersions(deviceVer, selectedVer.assemblyVersion) < 0;
  }

  deviceVersionStatus(device: CompatibleDevice): 'older' | 'equal' | 'newer' | 'none' {
    const selectedVer = this.selectedVersion();
    if (!selectedVer) return 'none';

    const deviceVer = device.version;
    if (!deviceVer) return 'none';

    const cmp = this.compareVersions(deviceVer, selectedVer.assemblyVersion);
    if (cmp < 0) return 'older';
    if (cmp === 0) return 'equal';
    return 'newer';
  }



  uploadPackage(): void {
    const ns = this.uploadNamespace() || this.selectedNamespace();
    const file = this.uploadFile();
    if (!ns || !file) return;

    this.uploading.set(true);
    this.api.uploadPackage(ns.id, file, this.uploadReleaseNotes || undefined)
      .then(result => {
        this.messageService.add({
          severity: 'success',
          summary: 'Upload Complete',
          detail: `Uploaded ${result.name} v${result.version}`
        });
        this.showUploadDialog.set(false);
        this.uploading.set(false);
        
        // Auto-select the newly uploaded package
        this.pendingSelectPkg.set({ name: result.name, nsId: ns.id });
        this.loadPackages();
      })
      .catch(err => {
        console.error('Upload failed', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Upload Failed',
          detail: err?.error?.message || err?.error || 'Failed to upload package'
        });
        this.uploading.set(false);
      });
  }

  cancelUpload(): void {
    this.showUploadDialog.set(false);
    this.uploadFile.set(null);
    this.uploadReleaseNotes = '';
  }

  // Edit package
  openEditDialog(pkg: Package): void {
    this.editPackageName = pkg.name;
    this.editPackageDescription = pkg.description || '';
    this.editPackageRepository = pkg.gitHubRepository || '';
    this.showEditDialog.set(true);
  }

  /** The user may connect GitHub for the package's tenant (namespace.manage). */
  canConfigureTenant(pkg: Package): boolean {
    return this.api.hasCapability(pkg.appNamespaceId, 'namespace.manage');
  }

  saveEdit(): void {
    const pkg = this.selectedPackage()?.package;
    if (!pkg) return;

    const repository = normalizeGitHubRepository(this.editPackageRepository);
    if (repository === null) {
      this.messageService.add({ severity: 'warn', summary: this.t()('github.repository'), detail: this.t()('github.repositoryInvalid') });
      return;
    }

    this.savingEdit.set(true);
    this.api.updatePackage(pkg.id, this.editPackageName, this.editPackageDescription, repository)
      .then(updated => {
        this.messageService.add({
          severity: 'success',
          summary: 'Updated',
          detail: 'Package updated successfully'
        });
        this.showEditDialog.set(false);
        this.savingEdit.set(false);
        // Refresh package details
        this.viewPackage(updated);
        this.loadPackages();
      })
      .catch(err => {
        console.error('Failed to update package', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.error ?? (typeof err?.error === 'string' ? err.error : null) ?? 'Failed to update package'
        });
        this.savingEdit.set(false);
      });
  }

  // Delete package
  deletePackage(pkg: Package): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${pkg.name}"? This will delete all versions and cannot be undone.`,
      header: 'Delete Package',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        this.loading.set(true);
        try {
          await this.api.deletePackage(pkg.id);
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: `Package "${pkg.name}" deleted`
          });
          this.closePackageDialog();
          this.loadPackages();
        } catch (err: any) {
          console.error('Failed to delete package', err);
          const inUse = versionInUseMessage(err);
          this.messageService.add({
            severity: inUse ? 'warn' : 'error',
            summary: inUse ? 'Package in use' : 'Error',
            detail: inUse ?? (err?.error || 'Failed to delete package'),
            life: inUse ? 10000 : undefined
          });
          this.loading.set(false);
        }
      }
    });
  }

  // Set version as latest
  async setLatestVersion(version: PackageVersion): Promise<void> {
    const pkg = this.selectedPackage()?.package;
    if (!pkg) return;

    try {
      await this.api.updatePackageVersion(pkg.id, version.id, undefined, true);
      this.messageService.add({
        severity: 'success',
        summary: 'Updated',
        detail: `Version ${version.assemblyVersion} set as latest`
      });
      // Refresh package details
      this.viewPackage(pkg);
    } catch (err: any) {
      console.error('Failed to set latest version', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: err?.error || 'Failed to update version'
      });
    }
  }

  // Delete version
  deleteVersion(version: PackageVersion): void {
    const pkg = this.selectedPackage()?.package;
    if (!pkg) return;

    this.confirmationService.confirm({
      message: `Delete version ${version.assemblyVersion}? This cannot be undone.`,
      header: 'Delete Version',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          await this.api.deletePackageVersion(pkg.id, version.id);
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: `Version ${version.assemblyVersion} deleted`
          });
          // Refresh package details
          this.viewPackage(pkg);
        } catch (err: any) {
          console.error('Failed to delete version', err);
          const inUse = versionInUseMessage(err);
          this.messageService.add({
            severity: inUse ? 'warn' : 'error',
            summary: inUse ? 'Version in use' : 'Error',
            detail: inUse ?? (err?.error || 'Failed to delete version'),
            life: inUse ? 10000 : undefined
          });
          // A version that turned out to be in use gets its badge.
          if (inUse) void this.loadVersions();
        }
      }
    });
  }

  // Toggle version selection
  toggleVersionSelection(version: SelectableVersion): void {
    if (version.usage?.inUse) return;
    const selected = new Map(this.selectedVersions());
    const { selected: _, ...plain } = version;
    if (selected.has(version.id)) selected.delete(version.id);
    else selected.set(version.id, plain);
    this.selectedVersions.set(selected);
  }

  // Toggle selection of every version on the current page
  toggleAllVersions(): void {
    const allSelected = this.allVersionsSelected();
    const selected = new Map(this.selectedVersions());
    for (const { selected: _, ...version } of this.displayedVersions()) {
      if (version.usage?.inUse) continue;
      if (allSelected) selected.delete(version.id);
      else selected.set(version.id, version);
    }
    this.selectedVersions.set(selected);
  }

  // Delete selected versions
  deleteSelectedVersions(): void {
    const pkg = this.selectedPackage()?.package;
    if (!pkg) return;

    const selectedVersions = [...this.selectedVersions().values()];
    if (selectedVersions.length === 0) return;

    // A selection can span many pages; name the first few rather than every version.
    const shown = selectedVersions.slice(0, 10).map(v => v.assemblyVersion).join(', ');
    const versionList = selectedVersions.length > 10 ? `${shown} and ${selectedVersions.length - 10} more` : shown;

    this.confirmationService.confirm({
      message: `Delete ${selectedVersions.length} version(s): ${versionList}? This cannot be undone.`,
      header: 'Delete Selected Versions',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        this.deletingVersions.set(true);

        // Delete all versions in parallel for faster execution
        const results = await Promise.allSettled(
          selectedVersions.map(version =>
            this.api.deletePackageVersion(pkg.id, version.id)
          )
        );

        const successCount = results.filter(r => r.status === 'fulfilled').length;
        const inUseCount = results.filter(r => r.status === 'rejected' && versionInUseMessage(r.reason) !== null).length;
        const failCount = results.filter(r => r.status === 'rejected').length - inUseCount;

        this.deletingVersions.set(false);

        if (successCount > 0) {
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: `${successCount} version(s) deleted successfully`
          });
        }

        if (inUseCount > 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Kept versions in use',
            detail: `${inUseCount} version(s) are running on devices or have pending deployments and were not deleted`
          });
        }

        if (failCount > 0) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to delete ${failCount} version(s)`
          });
        }

        // Refresh package details
        this.viewPackage(pkg);
      }
    });
  }

  openCleanupDialog(): void {
    ++this.cleanupRequest;
    this.cleanupBefore.set(null);
    this.cleanupDebug.set(false);
    this.cleanupKeepNewest.set(null);
    this.cleanupPreview.set(null);
    this.cleanupPreviewLoading.set(false);
    this.cleanupPreviewError.set(false);
    this.showCleanupDialog.set(true);
  }

  setCleanupBefore(value: Date | null): void {
    this.cleanupBefore.set(value);
    void this.refreshCleanupPreview();
  }

  setCleanupDebug(value: boolean): void {
    this.cleanupDebug.set(!!value);
    void this.refreshCleanupPreview();
  }

  setCleanupKeepNewest(value: number | null): void {
    this.cleanupKeepNewest.set(value != null && value >= 1 ? Math.floor(value) : null);
    void this.refreshCleanupPreview();
  }

  /** Asks the server how many versions the current criteria match across the whole history. */
  async refreshCleanupPreview(): Promise<void> {
    const packageId = this.selectedPackage()?.package.id;
    const request = ++this.cleanupRequest;
    this.cleanupPreview.set(null);
    this.cleanupPreviewError.set(false);
    if (!packageId || !this.canCleanUpVersions()) {
      this.cleanupPreviewLoading.set(false);
      return;
    }
    this.cleanupPreviewLoading.set(true);
    try {
      const preview = await this.api.cleanUpPackageVersions(packageId, this.cleanupCriteria(true));
      if (request === this.cleanupRequest) this.cleanupPreview.set(preview);
    } catch (err) {
      if (request !== this.cleanupRequest) return;
      console.error('Failed to preview version clean-up', err);
      this.cleanupPreviewError.set(true);
    } finally {
      if (request === this.cleanupRequest) this.cleanupPreviewLoading.set(false);
    }
  }

  private cleanupCriteria(dryRun: boolean) {
    return { uploadedBefore: this.cleanupBefore()?.toISOString() ?? null, includeDebug: this.cleanupDebug(), keepNewest: this.cleanupKeepNewest(), dryRun };
  }

  onDetailTabChange(tab: string | number | undefined): void {
    if ((PACKAGE_TABS as readonly (string | number)[]).includes(tab ?? '')) {
      this.activeDetailTab.set(tab as PackageTab);
    }
  }

  cleanUpVersions(): void {
    const pkg = this.selectedPackage()?.package;
    const matched = this.cleanupPreview()?.matched ?? 0;
    if (!pkg || matched === 0 || !this.canCleanUpVersions()) return;

    this.confirmationService.confirm({
      message: `Delete ${matched} matching version(s)? The latest version is always kept. This cannot be undone.`,
      header: 'Clean Up Versions',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        this.cleanupInProgress.set(true);
        try {
          const result = await this.api.cleanUpPackageVersions(pkg.id, this.cleanupCriteria(false));
          this.showCleanupDialog.set(false);
          if (result.deleted > 0) {
            this.messageService.add({ severity: 'success', summary: 'Clean-up complete', detail: `${result.deleted} version(s) deleted` });
          } else if (result.failed === 0 && result.skippedInUse.length === 0) {
            this.messageService.add({ severity: 'info', summary: 'Clean-up complete', detail: 'No versions matched' });
          }
          if (result.skippedInUse.length > 0) {
            this.messageService.add({ severity: 'info', summary: 'Kept versions in use', detail: `${result.skippedInUse.length} matching version(s) are running on devices or have pending deployments and were kept` });
          }
          if (result.failed > 0) {
            this.messageService.add({ severity: 'error', summary: 'Clean-up incomplete', detail: `Failed to delete ${result.failed} version(s)` });
          }
          this.viewPackage(pkg);
        } catch (err: any) {
          console.error('Version clean-up failed', err);
          this.messageService.add({ severity: 'error', summary: 'Clean-up failed', detail: err?.error?.error || err?.error || 'Failed to clean up versions' });
        } finally {
          this.cleanupInProgress.set(false);
        }
      }
    });
  }

  // Deploy
  deployLatestVersion(): void {
    const latestVersion = this.selectedPackage()?.latestVersion;
    if (latestVersion) {
      this.openDeployDialog({ ...latestVersion, isLatest: true });
    }
  }

  openDeployDialog(version: PackageVersion): void {
    this.selectedVersion.set(version);
    this.compatibleDevices.set([]);
    this.showDeployDialog.set(true);
    this.loadCompatibleDevices();
  }

  private loadCompatibleDevices(): void {
    const pkg = this.selectedPackage()?.package;
    if (!pkg) return;

    this.loadingDevices.set(true);
    this.api.getCompatibleDevices(pkg.id)
      .then(async (devices) => {
        const preSelectLocId = this.preSelectLocationId();
        const parentDeviceIds = new Set<string>();

        if (preSelectLocId) {
          try {
            const locDetails = await this.api.getLocationDetails(preSelectLocId);
            if (locDetails && locDetails.parentDevices) {
              locDetails.parentDevices.forEach((d: any) => {
                if (d.id) {
                  parentDeviceIds.add(d.id);
                }
              });
            }
          } catch (err) {
            console.error('Failed to load location details in loadCompatibleDevices', err);
          }
        }

        const subtreeIds = preSelectLocId ? this.getLocationSubtreeIds(preSelectLocId) : null;
        const selectableDevices: SelectableDevice[] = devices.map(d => ({
          ...d,
          selected: (subtreeIds ? (!!d.locationNodeId && subtreeIds.has(d.locationNodeId) || parentDeviceIds.has(d.id)) : false) && this.deviceNeedsUpdate(d)
        }));
        this.compatibleDevices.set(selectableDevices);
        this.deviceFilter.set('');
        this.loadingDevices.set(false);
      })
      .catch(err => {
        console.error('Failed to load compatible devices', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error || 'Failed to load compatible devices'
        });
        this.loadingDevices.set(false);
      });
  }

  private getLocationSubtreeIds(locationId: string): Set<string> {
    const locations = (this.api.locations() || []) as NamespaceLocation[];
    const result = new Set<string>();
    const visit = (id: string) => {
      result.add(id);
      locations.filter(l => l.parentId === id).forEach(l => visit(l.id));
    };
    visit(locationId);
    return result;
  }

  toggleAllDevices(): void {
    const visibleDevices = this.filteredCompatibleDevices();
    const visibleDeviceIds = new Set(visibleDevices.map(d => d.id));
    const currentDevices = this.compatibleDevices();
    
    const needsUpdateDevices = visibleDevices.filter(d => this.deviceNeedsUpdate(d));
    const allSelected = needsUpdateDevices.length > 0
      && needsUpdateDevices.every(d => d.selected);

    const newDevices = currentDevices.map(d => {
      if (visibleDeviceIds.has(d.id)) {
        if (allSelected) {
          return { ...d, selected: false };
        } else {
          return { ...d, selected: this.deviceNeedsUpdate(d) };
        }
      }
      return d;
    });
    this.compatibleDevices.set(newDevices);
  }

  toggleDeviceSelection(device: SelectableDevice): void {
    if (!this.deviceNeedsUpdate(device)) return;
    const currentDevices = this.compatibleDevices();
    const newDevices = currentDevices.map(d =>
      d.id === device.id ? { ...d, selected: !d.selected } : d
    );
    this.compatibleDevices.set(newDevices);
  }

  toggleLocationSelection(locationRow: LocationDeviceRow): void {
    const subtreeIds = new Set(locationRow.devicesInSubtree.map(d => d.id));
    const needsUpdateDevices = locationRow.devicesInSubtree.filter(d => this.deviceNeedsUpdate(d));
    const allSelected = needsUpdateDevices.length > 0
      && needsUpdateDevices.every(d => d.selected);

    const updated = this.compatibleDevices().map(d => {
      if (subtreeIds.has(d.id)) {
        if (allSelected) {
          return { ...d, selected: false };
        } else {
          return { ...d, selected: this.deviceNeedsUpdate(d) };
        }
      }
      return d;
    });
    this.compatibleDevices.set(updated);
  }

  isLocationFullySelected(locationRow: LocationDeviceRow): boolean {
    const devices = locationRow.devicesInSubtree.filter(d => this.deviceNeedsUpdate(d));
    return devices.length > 0 && devices.every(d => d.selected);
  }

  isLocationPartiallySelected(locationRow: LocationDeviceRow): boolean {
    const devices = locationRow.devicesInSubtree.filter(d => this.deviceNeedsUpdate(d));
    const selected = devices.filter(d => d.selected).length;
    return selected > 0 && selected < devices.length;
  }

  async downloadVersion(version: PackageVersion): Promise<void> {
    const pkg = this.selectedPackage()?.package;
    if (!pkg) return;

    try {
      const details = await this.api.getPackageVersion(pkg.id, version.id);
      if (!details.downloadUrl) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Download Not Available',
          detail: 'No download URL is available for this package version.'
        });
        return;
      }

      window.open(details.downloadUrl, '_blank', 'noopener');
      this.messageService.add({
        severity: 'success',
        summary: 'Download Started',
        detail: `Started download for v${version.assemblyVersion}`
      });
    } catch (err: any) {
      console.error('Failed to download package version', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Download Failed',
        detail: err?.error || 'Failed to download package version'
      });
    }
  }

  async openConsoleCommandDialog(version: PackageVersion): Promise<void> {
    const pkg = this.selectedPackage()?.package;
    if (!pkg) return;

    this.consoleCommandVersion.set(version);
    this.consoleCommandProgramSlot.set(1);
    this.consoleCommandMode.set('quick');
    this.consoleCommandExpiry.set(60);
    this.showConsoleCommandDialog.set(true);
    await this.refreshConsoleCommand();
  }

  closeConsoleCommandDialog(): void {
    ++this.consoleRequest;
    this.showConsoleCommandDialog.set(false);
    this.consoleCommandDownloadUrl.set(null);
    this.consoleCommandExpiresUtc.set(null);
    this.consoleCommandLoading.set(false);
  }

  setConsoleCommandProgramSlot(value: number | string | null): void {
    this.consoleCommandProgramSlot.set(clampProgramSlot(typeof value === 'string' ? parseInt(value, 10) : value));
  }

  async refreshConsoleCommand(): Promise<void> {
    const pkg = this.selectedPackage()?.package;
    const version = this.consoleCommandVersion();
    if (!pkg || !version) return;
    const request = ++this.consoleRequest;
    this.consoleCommandDownloadUrl.set(null);
    this.consoleCommandExpiresUtc.set(null);
    this.consoleCommandError.set(false);
    if (this.consoleCommandMode() !== 'quick') {
      this.consoleCommandLoading.set(false);
      return;
    }
    this.consoleCommandLoading.set(true);
    try {
      const requestedAt = Date.now();
      const details = await this.api.getPackageVersion(pkg.id, version.id, this.consoleCommandExpiry());
      if (request !== this.consoleRequest) return;
      if (!details.downloadUrl) throw new Error('Download URL unavailable');
      this.consoleCommandDownloadUrl.set(details.downloadUrl);
      this.consoleCommandExpiresUtc.set(new Date(requestedAt + (details.expiresInMinutes ?? this.consoleCommandExpiry()) * 60000).toISOString());
    } catch {
      if (request === this.consoleRequest) this.consoleCommandError.set(true);
    } finally {
      if (request === this.consoleRequest) this.consoleCommandLoading.set(false);
    }
  }

  /** Hands off to the share-links dialog, preselecting a link pinned to the version in the console dialog. */
  createShareableLink(): void {
    const version = this.consoleCommandVersion();
    const links = this.packageLinks();
    if (!version || !links) return;
    this.closeConsoleCommandDialog();
    links.openCreate(version);
  }

  copyToClipboard(text: string): void {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      this.messageService.add({ severity: 'success', summary: this.t()('console.copied'), life: 2000 });
    }).catch(() => {
      this.messageService.add({ severity: 'error', summary: this.t()('console.copyFailed'), life: 3000 });
    });
  }

  deployToDevices(): void {
    const pkg = this.selectedPackage()?.package;
    const version = this.selectedVersion();
    const selectedDevices = this.compatibleDevices().filter(d => d.selected);

    if (!pkg || !version || selectedDevices.length === 0) return;

    this.deploying.set(true);

    const deviceIds = selectedDevices.map(d => d.id);

    this.api.deployPackageToMultiple(pkg.id, version.id, deviceIds)
      .then(result => {
        if (result.totalFailed === 0) {
          this.messageService.add({
            severity: 'success',
            summary: 'Deployment Scheduled',
            detail: `Deploying ${pkg.name} v${version.assemblyVersion} to ${result.totalSuccessful} device(s)`
          });
        } else if (result.totalSuccessful > 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Partial Deployment',
            detail: `Deployed to ${result.totalSuccessful} device(s), ${result.totalFailed} failed`
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Deployment Failed',
            detail: `Failed to deploy to all ${result.totalFailed} device(s)`
          });
        }
        this.showDeployDialog.set(false);
        this.deploying.set(false);
      })
      .catch(err => {
        console.error('Deployment failed', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Deployment Failed',
          detail: err?.error || 'Failed to deploy package'
        });
        this.deploying.set(false);
      });
  }

  deployToDevicesScheduled(): void {
    const pkg = this.selectedPackage()?.package;
    const version = this.selectedVersion();
    const selectedDevices = this.compatibleDevices().filter(d => d.selected);

    if (!pkg || !version || selectedDevices.length === 0 || !this.scheduledDateTime) return;

    this.deploying.set(true);

    const deviceIds = selectedDevices.map(d => d.id);
    const scheduledForUtc = this.scheduledDateTime.toISOString();

    this.api.deployPackageToMultiple(pkg.id, version.id, deviceIds, scheduledForUtc)
      .then(result => {
        const scheduledTime = this.scheduledDateTime!.toLocaleString();
        if (result.totalFailed === 0) {
          this.messageService.add({
            severity: 'success',
            summary: 'Deployment Scheduled',
            detail: `Scheduled ${pkg.name} v${version.assemblyVersion} for ${result.totalSuccessful} device(s) at ${scheduledTime}`
          });
        } else if (result.totalSuccessful > 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Partial Scheduling',
            detail: `Scheduled for ${result.totalSuccessful} device(s), ${result.totalFailed} failed`
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Scheduling Failed',
            detail: `Failed to schedule for all ${result.totalFailed} device(s)`
          });
        }
        this.showScheduleDialog.set(false);
        this.showDeployDialog.set(false);
        this.scheduledDateTime = null;
        this.deploying.set(false);
      })
      .catch(err => {
        console.error('Scheduled deployment failed', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Scheduling Failed',
          detail: err?.error || 'Failed to schedule deployment'
        });
        this.deploying.set(false);
      });
  }

  formatRelativeTime(dateStr: string | null | undefined): string {
    if (!dateStr) return 'Never';
    try {
      const date = this.parseUtcDate(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  }

  private parseUtcDate(dateStr: string): Date {
    if (TIMEZONE_SUFFIX_REGEX.test(dateStr)) {
      return new Date(dateStr);
    }
    return new Date(`${dateStr}Z`);
  }

  // Utility
  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return 'N/A';
    try {
      return this.parseUtcDate(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
